import React from "react";
import { FormikCheckboxInput } from "components/UI/Inputs/FormikInputs";
import styles from "./PermisosDeReparticion.module.scss";

/**
 * Los dos permisos de una repartición: si admite horarios ROTATIVOS y si admite GUARDIAS.
 *
 * Van SEPARADOS y no como una sola opción porque son independientes: una repartición puede
 * admitir guardias sin admitir rotativos, y al revés.
 *
 * VIVE ACÁ Y NO EN CADA MODAL porque lo van a llevar las once vistas de estructuras, en el
 * alta y en la edición. Sumarlo a una vista nueva son tres líneas: este componente, el
 * spread en `initialValues`, y el mapeo en el servicio.
 *
 *   // alta
 *   initialValues={{ ...otros, ...PERMISOS_INICIALES }}
 *
 *   // edición
 *   initialValues={{ ...otros, ...permisosDesdeRow(row) }}
 *
 *   <Grid item xs={12}>
 *     <PermisosDeReparticion />
 *   </Grid>
 *
 * GUARDA "Y"/"N" Y NO BOOLEANOS. Es la convención del módulo para las banderas
 * —`rnhAgenteEstablecimientoUnico`, `rnhLugarPrincipal`, `rnhTituloRequiereMatricula`—, así
 * que el valor de Formik ya es el que va a necesitar el servicio y no hay que traducir en
 * el submit.
 *
 * LOS CAMPOS SE LLAMAN COMO LOS PARAMS DEL BACK: `admiteRotativos` y `admiteGuardias`, no
 * "permite". Tener dos palabras para el mismo concepto a los dos lados del servicio es la
 * clase de roce que termina en un campo que no viaja y nadie entiende por qué.
 *
 * Del otro lado el prefijo cambia según la tabla:
 *
 *   rnhDepartamentoAdmiteRotativos / ...AdmiteGuardias
 *   rnhDivisionAdmite...   rnhUnidadAdmite...
 *   rnhEstablecimientoAdmite...   — UNA SOLA para los cinco tipos
 *
 * EN HOSPITAL Y URESA LOS DECIDE EL BACK, que valida contra el tipo de establecimiento.
 * En CAPS, puestos y postas vale lo que mandemos. Los servicios ya aceptan los dos params
 * en los ocho `Ins`/`Upd`.
 *
 * CADA MODAL TIENE QUE PASARLOS EN SU SUBMIT: el componente solo los pone en el estado de
 * Formik, no sabe nada del servicio. Y el loader de cada vista tiene que leer las dos
 * claves de la response, o la edición abre con los dos checkbox en blanco aunque el back
 * diga que sí.
 */

/**
 * Dos etiquetas por permiso, y cuál se usa lo decide el ANCHO, no el gusto.
 *
 * La larga es la que se lee mejor: dice qué significa marcar la casilla. Se usa en los
 * modales de repartición, que son de una columna ancha y tienen lugar de sobra.
 *
 * La corta existe por una restricción concreta: en los establecimientos el formulario
 * comparte el modal con el mapa, y la columna de carga queda en unos 330px. Con el verbo
 * adelante las dos casillas suman ~440px, `flex-wrap` las apila, y el renglón que habíamos
 * liberado para ellas deja de alcanzar.
 *
 * Por eso no es un prop que elija cada pantalla: lo pasa `EstablecimientoFormulario`, que
 * es el único que sabe que su columna es angosta. Así la decisión vive junto al layout que
 * la obliga, y una vista nueva no tiene que adivinar cuál le toca.
 */
const PERMISOS = [
    {
        name: "admiteRotativos",
        label: "Permite horarios rotativos",
        labelCorto: "Horarios rotativos",
    },
    {
        name: "admiteGuardias",
        label: "Permite guardias",
        labelCorto: "Guardias",
    },
];

export const PERMISOS_INICIALES = {
    admiteRotativos: "N",
    admiteGuardias: "N",
};

/**
 * Los permisos de una fila del listado, para precargar la edición.
 *
 * Con `?? "N"` y no `?? ""`: mientras el back no los mande, la fila no los trae y el
 * checkbox tiene que arrancar en algún lado. "N" es el default correcto —una repartición
 * no admite nada hasta que alguien lo diga— y además es lo que el back va a esperar.
 */
export const permisosDesdeRow = (row) => ({
    admiteRotativos: row?.admiteRotativos ?? "N",
    admiteGuardias: row?.admiteGuardias ?? "N",
});

/**
 * @param compacto etiquetas sin el "Permite", para una columna angosta. Ver PERMISOS.
 */
export default function PermisosDeReparticion({ disabled = false, compacto = false }) {
    return (
        <div className={styles.permisos}>
            <div className={styles.casillas}>
                {PERMISOS.map(({ name, label, labelCorto }) => (
                    <FormikCheckboxInput
                        key={name}
                        name={name}
                        labelText={compacto ? labelCorto : label}
                        disabled={disabled}
                    />
                ))}
            </div>
        </div>
    );
}
